<template>
  <div>
    <h4 class="mb-4">
      Tenants
    </h4>

    <v-snackbar
      v-model="snackbar"
      :timeout="5000"
      :top="true"
      color="success"
    >
      {{ snackbarMessage }}
    </v-snackbar>

    <u-loader v-if="isLoading" />

    <div v-else>
      <v-btn
        v-if="filterUnusedTenants().length && canUserEdit"
        color="success"
        @click="openCreateModal"
      >
        <i class="fas fa-plus mr-2" />
        Neuer Tenant erfassen
      </v-btn>
      <v-combobox
        v-model="selectedLangs"
        :items="langs"
        chips
        class="col-4 mt-4"
        clearable
        item-text="name"
        item-value="ref"
        label="Sprachfilter"
        multiple
      />

      <tenant-create-modal
        v-if="isCreateModalOpen"
        :aviable-tenants="filterUnusedTenants()"
        :contract-id="contractId"
        :langs="langs"
        :show-modal="isCreateModalOpen"
        :version-id="versionIdId"
        :version-number="versionId"
        @success="handleCreateSuccess"
        @close-modal="closeCreateModal"
      />

      <b-card no-body>
        <b-tabs
          v-model="currentNavigationTab"
          card
          class="tenants-tabs"
          pills
          vertical
        >
          <b-tab title="Was ist neu">
            <b-card-text>
              <tenant-whats-new-section
                :all-tenants="allTenants"
                :available-tenants="availableTenants"
                :can-user-edit="canUserEdit && ($IsUserInRole('VertragsmanagementVerwaltung') || $IsUserInRole('VertragsmanagementUniaVertragsverantworlich'))"
                :contract-id="contractId"
                :lang-filter="getSelectedLangs"
                :version-id="versionIdId"
                @save="handleSaveWhatsNew"
              />
            </b-card-text>
          </b-tab>
          <b-tab
            v-for="(tenant, index) in availableTenants"
            :key="tenant.id"
            :title="getTenantTitle(tenant.tenantId)"
            :disabled="
              $IsUserInRole('VertragsmanagementUniaVertragsverantworlich') &&
                !$IsUserInRole('VertragsmanagementVerwaltung') &&
                tenant.tenantId !== 4
            "
          >
            <b-card-text>
              <tenant-base-layout
                :can-user-edit="canUserEdit"
                :error-object="errorObject"
                :is-loading="isLoading"
                @delete="deleteTenant(tenant.id)"
                @save="saveTenant(tenant)"
                @cancel-edit="cancelTenantEdit(tenant.id)"
              >
                <!-- Was ist neu Section -->
                <b-row class="mt-4">
                  <b-col :cols="firstColumnWidth">
                    <label
                      class="font-weight-bold mt-3"
                      for="tenantId"
                    >Was ist neu</label>
                  </b-col>

                  <b-col :cols="12 - firstColumnWidth">
                    <v-expansion-panel
                      v-for="(lang, langIndex) in getSelectedLangs"
                      :key="langIndex"
                      v-model="panelStates[langIndex]"
                    >
                      <div
                        v-if="canUserEdit"
                        class="w-100"
                      >
                        <v-expansion-panel-content
                          v-if="
                            tenant.publicationContents.find(
                              (content) => content.language === lang.ref,
                            )
                          "
                        >
                          <template v-slot:header>
                            <div>
                              <h6>{{ lang.name }}</h6>
                              <div v-if="panelStates[langIndex] !== 0">
                                {{
                                  tenant.publicationContents.find(
                                    (content) => content.language === lang.ref,
                                  ).news
                                }}
                              </div>
                            </div>
                          </template>
                          <v-card class="w-100">
                            <v-card-text>
                              <b-form-textarea
                                v-model="
                                  tenant.publicationContents.find(
                                    (content) => content.language === lang.ref,
                                  ).news
                                "
                                :rows="
                                  calcRows(
                                    tenant.publicationContents.find(
                                      (content) =>
                                        content.language === lang.ref,
                                    ).news,
                                  )
                                "
                              />
                            </v-card-text>
                          </v-card>
                        </v-expansion-panel-content>
                      </div>
                      <div
                        v-else-if="
                          tenant.publicationContents.find(
                            (content) => content.language === lang.ref,
                          ).news
                        "
                      >
                        <b>{{ lang.name }}</b>
                        <br>
                        {{
                          tenant.publicationContents.find(
                            (content) => content.language === lang.ref,
                          ).news
                        }}
                      </div>
                      <p v-else>
                        -
                      </p>
                    </v-expansion-panel>
                  </b-col>
                </b-row>
                <hr>
                <!-- Anzeigename Section -->
                <tenant-multi-lang-form-section
                  :can-user-edit="canUserEdit"
                  :first-column-width="firstColumnWidth"
                  :lang-filter="getSelectedLangs"
                  :model="tenant.publicationContents"
                  field="value"
                  label="Anzeigename"
                />
                <hr>
                <!-- Kurzname Section -->
                <tenant-multi-lang-form-section
                  :can-user-edit="canUserEdit"
                  :first-column-width="firstColumnWidth"
                  :lang-filter="getSelectedLangs"
                  :model="tenant.publicationContents"
                  field="shortName"
                  label="Kurzname"
                />
                <hr>
                <!-- Publikationsdatum Section -->
                <b-row>
                  <b-col :cols="firstColumnWidth">
                    <label
                      class="font-weight-bold"
                      for="publikationsdatum"
                    >Publikationsdatum</label>
                  </b-col>
                  <b-col cols="9">
                    <v-flex
                      v-if="canUserEdit"
                      md6
                      sm12
                    >
                      <u-date-picker
                        :id="'tenantPublishingDateInput' + index"
                        v-model="tenant.publicationDate"
                        label="Publikationsdatum"
                      />
                    </v-flex>

                    <p v-else-if="formatDateTime(tenant.publicationDate)">
                      {{ formatDateTime(tenant.publicationDate) }}
                    </p>
                    <p v-else>
                      -
                    </p>
                  </b-col>
                </b-row>
                <hr>
                <!-- Gültig ab Section -->
                <b-row>
                  <b-col :cols="firstColumnWidth">
                    <label
                      class="font-weight-bold"
                      for="gueltigab"
                    >Gültig ab</label>
                  </b-col>
                  <b-col cols="9">
                    <v-flex
                      v-if="canUserEdit"
                      md6
                      sm12
                    >
                      <u-date-picker
                        :id="'tenantValidFromInput' + index"
                        v-model="tenant.validFrom"
                        label="Gültig ab"
                      />
                    </v-flex>
                    <p v-else-if="formatDateTime(tenant.validFrom)">
                      {{ formatDateTime(tenant.validFrom) }}
                    </p>
                    <p v-else>
                      -
                    </p>
                  </b-col>
                </b-row>
                <hr>
                <!-- RSS Section -->
                <b-row>
                  <b-col>
                    <v-expansion-panel>
                      <v-expansion-panel-content>
                        <template v-slot:header>
                          <label class="font-weight-bold mr-4">RSS-Feed</label>
                        </template>
                        <v-card>
                          <div>
                            <div>
                              <rss-feed-section
                                :can-user-edit="canUserEdit"
                                :contract-id="contractId"
                                :lang-filter="getSelectedLangs"
                                :version-id="versionIdId"
                                :tenant="tenant"
                                @updateFeedEntry="handleFeedEntryChange"
                              />
                            </div>
                          </div>
                        </v-card>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </b-col>
                </b-row>
              </tenant-base-layout>
            </b-card-text>
          </b-tab>
        </b-tabs>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  DeletePublication,
  GetAllPublications,
  GetAvailableTenants,
  UpdatePublication,
} from "@/services/contract.tenants.service";
import { UpdateFeedEntry } from "@/services/rssFeed.service";
import UDatePicker from "@/components/common/UDatePicker.vue";
import TenantMultiLangFormSection from "@/components/contract/version/tenant/MultiLangFormSection.vue";
import TenantWhatsNewSection from "@/components/contract/version/tenant/WhatsNewSection.vue";
import TenantBaseLayout from "@/components/contract/version/tenant/BaseLayout.vue";
import TenantCreateModal from "@/components/contract/version/tenant/CreateModal.vue";
import RssFeedSection from "@/components/contract/version//tenant/RssFeedSection.vue";

// Wrapper für Callback-basierte Funktionen
function GetAvailableTenantsAsync() {
  return new Promise((resolve, reject) => {
    GetAvailableTenants(resolve, reject);
  });
}

function GetAllPublicationsAsync(contractId, versionIdId) {
  return new Promise((resolve, reject) => {
    GetAllPublications(contractId, versionIdId, resolve, reject);
  });
}

export default {
  components: {
    TenantBaseLayout,
    TenantMultiLangFormSection,
    UDatePicker,
    TenantWhatsNewSection,
    TenantCreateModal,
    RssFeedSection,
  },
  props: {
    contractId: { type: String, default: "" },
    versionId: { type: Number, default: 0 },
    versionIdId: { type: Number, default: 0 },
    editionId: { type: Number, default: 0 },
    canUserEdit: { type: Boolean, default: false },
    selectedLanguages: { type: Array, default: () => [] },
  },
  data() {
    return {
      errorObject: null,
      isLoading: false,
      availableTenants: [],
      originalAvailableTenants: [],
      allTenants: [],
      firstColumnWidth: 2,
      isCreateModalOpen: false,
      snackbar: false,
      snackbarMessage: "",
      currentNavigationTab: 0,
      panelStates: [],
      rssFeedEntries: [],
      langs: [
        {
          ref: "de-CH",
          name: "Deutsch",
        },
        {
          ref: "fr-CH",
          name: "Französisch",
        },
        {
          ref: "it-CH",
          name: "Italienisch",
        },
      ],
      selectedLangs: this.langs ? this.langs : [],
    };
  },
  computed: {
    getSelectedLangs() {
      if (this.selectedLangs.length === 0) {
        return this.langs;
      }
      return this.selectedLangs;
    },
  },
  watch: {
    selectedLanguages: {
      handler: function () {
        if (this.langs && this.langs.length > 0) {
          const foundLang = this.selectedLanguages
            .map((selcLang) => {
              return this.langs.find(
                (lang) => lang.ref === selcLang.language && selcLang.selected,
              );
            })
            .filter((lang) => lang !== undefined);

          if (foundLang && foundLang.length > 0 && foundLang !== this.langs) {
            this.langs = foundLang;
            this.selectedLangs = foundLang;
            this.panelStates = foundLang.map(() => null);
          }
        }
      },
      immediate: true,
    },
  },

  created() {
    this.loadAvailableTenants();
    this.panelStates = this.langs.map(() => null);
  },
  methods: {
    calcRows(text) {
      if (!text) {
        return 1;
      }

      Math.ceil(text.length / 80);
    },
    openCreateModal() {
      this.isCreateModalOpen = true;
    },
    closeCreateModal() {
      this.isCreateModalOpen = false;
    },
    handleFeedEntryChange(changedFeedEntry) {
      let feedEntryIndex = this.rssFeedEntries.findIndex(
        (feedEntry) => feedEntry.id === changedFeedEntry.id,
      );

      this.rssFeedEntries = [
        ...this.rssFeedEntries.slice(0, feedEntryIndex),
        changedFeedEntry,
        ...this.rssFeedEntries.slice(feedEntryIndex + 1),
      ];
    },
    async loadAllTenants() {
      this.isLoading = true;
      this.errorObject = null;
      try {
        const publications = await GetAllPublicationsAsync(
          this.contractId,
          this.versionIdId,
        );
        publications.forEach((publication) => {
          // set the code to whatever, its not used but a required field.
          publication.code = "publicationTitle";
          if (
            !publication.publicationContents ||
            publication.publicationContents.length === 0
          ) {
            this.langs.forEach((language) => {
              publication.publicationContents.push({
                id: 0,
                shortName: "",
                news: "",
                rssFeed: "",
                publicationId: 0,
                value: "",
                language: language.ref,
              });
            });
          } else {
            publication.publicationContents = this.langs.map((lang) => {
              var content = publication.publicationContents.find(
                (content) => content.language === lang.ref,
              );
              if (!content) {
                content = {
                  id: 0,
                  shortName: "",
                  news: "",
                  rssFeed: "",
                  publicationId: 0,
                  value: "",
                  language: lang.ref,
                };
              }
              return content;
            });
          }
        });

        this.availableTenants = publications;
        this.originalAvailableTenants = JSON.parse(
          JSON.stringify(this.availableTenants),
        );
      } catch (error) {
        this.errorObject = error;
      } finally {
        this.isLoading = false;
      }
    },
    async loadAvailableTenants() {
      this.isLoading = true;
      this.errorObject = null;
      try {
        this.allTenants = await GetAvailableTenantsAsync();
        await this.loadAllTenants(); // Warte, bis alle Tenants geladen sind
      } catch (error) {
        this.errorObject = error;
      } finally {
        this.isLoading = false;
      }
    },
    filterUnusedTenants() {
      // Extrahiere die tenantIds aus den bereits verwendeten Tenants
      const usedTenantIds = this.availableTenants.map(
        (tenant) => tenant.tenantId,
      );

      // Filtere die Tenants, die nicht in usedTenantIds enthalten sind
      return this.allTenants.filter(
        (tenant) => !usedTenantIds.includes(tenant.id),
      );
    },
    getTenantTitle(tenantId) {
      const tenant = this.allTenants.find((t) => t.id === tenantId);
      return tenant ? tenant.bezeichnung : "";
    },
    deleteTenant(tenantId) {
      DeletePublication(
        tenantId,
        () => {
          this.snackbarMessage = "Tenant wurde erfolgreich gelöscht";
          this.snackbar = true;
          this.reloadTenants();
        },
        (error) => {
          this.errorObject = error;
        },
      );
    },
    saveTenant(tenant) {
      this.isLoading = true;
      this.errorObject = null;

      UpdatePublication(
        tenant.id,
        tenant,
        () => {
          let feedEntryPromises = [];
          this.rssFeedEntries.forEach((feedEntry) => {
            feedEntryPromises.push(
              new Promise((resolve, reject) => {
                UpdateFeedEntry(
                  feedEntry.id,
                  feedEntry,
                  () => {
                    resolve();
                  },
                  (error) => {
                    reject(error);
                  },
                );
              }),
            );
          });

          Promise.all(feedEntryPromises)
            .then(() => {
              this.snackbarMessage =
                "Änderungen wurden erfolgreich gespeichert";
              this.snackbar = true;
              this.isLoading = false;
              this.reloadTenants();
            })
            .catch((error) => {
              this.errorObject = error;
              this.isLoading = false;
            });
        },
        (error) => {
          this.errorObject = error;
          this.isLoading = false;
        },
      );
    },
    handleCreateSuccess() {
      this.snackbarMessage = "Tenant wurde erfolgreich erstellt";
      this.snackbar = true;
      this.reloadTenants();
    },
    handleSaveWhatsNew() {
      this.snackbarMessage = "Änderungen wurden erfolgreich gespeichert";
      this.snackbar = true;

      this.reloadTenants();
    },
    cancelTenantEdit(tenantId) {
      this.isLoading = true;
      const originalTenant = this.originalAvailableTenants.find(
        (t) => t.id === tenantId,
      );

      if (!originalTenant) {
        this.isLoading = false;
        return;
      }

      try {
        this.availableTenants = this.availableTenants.map((t) => {
          if (t.id === tenantId) {
            return JSON.parse(JSON.stringify(originalTenant));
          }
          return t;
        });
        this.isLoading = false;
      } catch (error) {
        this.errorObject = error;
        this.isLoading = false;
      }
    },
    reloadTenants() {
      this.loadAvailableTenants();
    },
    formatDateTime(date) {
      if (!date) {
        return "";
      }

      return new Date(date).toLocaleDateString("de-CH", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
  },
};
</script>

<style>
.tenants-tabs .nav-pills .nav-link.active,
.nav-pills .show > .nav-link {
  background-color: #e72b37 !important;
  color: #ffffff;
  font-weight: bold;
}

.custom-expansion-panel .v-expansion-panel__header {
  padding-left: 0 !important;
  padding-bottom: 0 !important;
  transition: background-color 0.5s linear;
}

.custom-expansion-panel {
  border: 0px solid #ffffff;
  box-shadow: none;
}

.custom-expansion-panel .v-expansion-panel__header :hover {
  background-color: rgba(0, 0, 0, 0.04);
  transition: background-color 0.5s linear;
}

.custom-expansion-panel .v-card__text {
  padding-top: 0;
}

.custom-expansion-panel .v-textarea {
  padding-top: 0 !important;
}
</style>
